/**
 * "Nearby cities" cross-link section for city / category × city pages.
 *
 * Static distance matrix between Morocco's main destinations. Renders top 4
 * closest cities with drive time + distance, each linking to that city's
 * /place/{slug} page. Builds out internal-link graph and gives users a
 * natural next step ("done with Marrakech, what's nearby?").
 *
 * Only cities whose hub has something on it are linked. An empty city hub is
 * `noindex`, and these links once sent Google to /place/fes (the city is
 * "fez") and /place/merzouga (nothing listed) from every page in seven
 * locales. The names come from the city rows, so they read in the page's
 * language.
 *
 * Distance/durations are approximate driving values along the standard route
 * — accurate enough for trip planning context, not turn-by-turn navigation.
 */
import { CONTROL_LIT } from "@majourneys/shared/ui";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { Icon } from "@majourneys/icons";
import { panelStyle } from "../../../shared/ui/card-frame";
import { NEARBY_CITIES as NEARBY } from "../lib/nearby-city-table";
import { stockedCities, type CityCountRow } from "../lib/hub-inventory";

const NAVY = "#110E39";
const MUTED = "#5a5d77";
/** A control at rest — the one definition, in `@majourneys/shared/ui`. */
const CONTROL_FILL = CONTROL_LIT;
const CONTROL_EDGE = "rgba(17,14,57,0.16)";

function formatHours(h: number): string {
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (Number.isInteger(h)) return `${h}h`;
  const whole = Math.floor(h);
  const minutes = Math.round((h - whole) * 60);
  return `${whole}h ${minutes}m`;
}

interface CategoryNearbyCitiesProps {
  citySlug?: string;
  cityName: string | null;
  lang: string;
  /** Every city with its counts; a city with nothing in it is left out. */
  cities: readonly CityCountRow[];
  limit?: number;
}

export function CategoryNearbyCities({
  citySlug,
  cityName,
  lang,
  cities,
  limit = 4,
}: CategoryNearbyCitiesProps) {
  const { t } = useTranslation();
  if (!citySlug || !cityName) return null;
  const stocked = new Map(stockedCities(cities).map((c) => [c.slug, c.name]));
  const items = (NEARBY[citySlug] ?? [])
    .filter((edge) => stocked.has(edge.slug))
    .slice(0, limit);
  if (items.length === 0) return null;
  const heading = t("activity.cities_near", { defaultValue: "Cities near {{city}}", city: cityName });

  return (
    <section
      aria-label={heading}
      className="py-10"
    >
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase" style={{ color: MUTED, letterSpacing: "0.09em" }}>
            {t("activity.plan_longer_trip", { defaultValue: "Plan a longer trip" })}
          </p>
          <h2
            className="mt-1 text-[20px] font-semibold leading-tight sm:text-[22px]"
            style={{ color: NAVY, fontFamily: "var(--font-geist)", letterSpacing: "-0.02em" }}
          >
            {heading}
          </h2>
        </div>
      </div>

      <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((edge) => {
          const label = stocked.get(edge.slug) ?? edge.slug;
          return (
            <li key={edge.slug}>
              <Link
                to="/$lang/place/$city"
                params={{ lang, city: edge.slug }}
                className="group flex items-center gap-3 p-4 transition-transform duration-200 hover:-translate-y-[2px]"
                style={panelStyle(22, undefined, "topLit")}
              >
                <span
                  aria-hidden
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-[20px] text-[color:var(--color-primary)]"
                  style={{ background: CONTROL_FILL, boxShadow: `0 0 0 1.5px ${CONTROL_EDGE}` }}
                >
                  <Icon name="ri-route-line" />
                </span>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span
                    className="text-[15px] font-semibold leading-tight group-hover:text-[color:var(--color-primary)]"
                    style={{ color: NAVY, letterSpacing: "-0.015em" }}
                  >
                    {label}
                  </span>
                  <span className="mt-0.5 text-[12.5px]" style={{ color: MUTED }}>
                    {formatHours(edge.hours)} · {edge.km} km
                  </span>
                </div>
                <Icon name="ri-arrow-right-line" className="text-[18px] text-[#5a5d77] transition group-hover:translate-x-0.5 rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5 group-hover:text-[color:var(--color-primary)]" aria-hidden />
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
